/**
 * Everything the game says when the camera stops working (GDS §36, CAM-05/CAM-06).
 *
 * GDS §36 names the *sequence* — pause, show a return-to-position overlay, resume without penalty,
 * offer an override or keyboard fallback — and not one word a family reads. So every string here
 * is invented and awaiting Jon's copy review (the D-049(5)/D-061(4)/D-063(5) pattern), and it is
 * declared in one place so `tests/architecture/recovery-copy.test.ts` can check that no undeclared
 * child-facing string has crept onto the stage.
 *
 * **Two registers, deliberately separated.** The overlay is for a five-year-old standing in their
 * living room: short, warm, and about the room rather than about them. It never says the camera
 * "failed", never says they did anything wrong, and never mentions the mission being wrong-footed —
 * because nothing is lost (MIN §13: an input problem is not a maths mistake, and D-021 extends to
 * this). The notice and the fallback offer are for the grown-up: plainer, honest about the machine,
 * and never technical enough to need explaining.
 *
 * **The cooperative group (Slice E′, D-076(2)).** Two children make two situations out of one, and
 * they are not the same situation: one child out of shot is a soft invitation that holds nothing,
 * and both children out of shot is the shipped pause. Two rules bind every word of it. It addresses
 * a hero by their own seat, never the other child — a sentence that mentioned the sibling would be
 * a comparison, and the non-competitive invariant's spirit reaches input quality (D-076(6), recon
 * §B.4). And it never claims the mission is waiting when it is not: in the soft state the round
 * genuinely carries on, so the promise made is the true one — the shards are safe and the spot is
 * kept.
 */

/** Copy no document contains. **Every string here is invented and awaiting approval.** */
export const INVENTED_RECOVERY_COPY = {
  /** The child-facing overlay: GDS §36's second rung. */
  overlay: {
    /** Overlay heading. Names the camera's problem, never the child's. */
    title: 'Where did you go?',
    /** What to do about it, in one instruction a five-year-old can follow. */
    body: 'Step back in front of the camera so we can see you again.',
    /** Why waiting is safe. This is the "resume without penalty" promise, said out loud. */
    reassurance: 'Your number shards are safe. We will carry on where we stopped.',

    /**
     * Nobody has arrived yet (Slice G, D-065(4)).
     *
     * "Where did you go?" is the wrong thing to say to a child who has not been anywhere, so the
     * same pause gets its own invitation. It asks rather than corrects, and it says out loud that
     * nothing is happening without them — because nothing is.
     */
    inviteTitle: 'Come and join in!',
    inviteBody: 'Stand in front of the camera so the suit can see you.',
    inviteReassurance: 'Nothing starts until you are ready.',

    /**
     * The camera itself stopped sending pictures (GDS §36's "Camera disconnected" row).
     *
     * Also the wrong moment for "step back in front of the camera": the child could be standing
     * exactly where they should be. It names the camera, gives the one useful instruction, and
     * points at the grown-up rather than at the five-year-old.
     */
    cameraStoppedTitle: 'The camera is having a rest',
    cameraStoppedBody: 'A grown-up can check the camera while we wait here.',
  },

  /** Grown-up-facing, on the stage: the CAM-06 degradation notice. */
  performance: {
    title: 'This computer is working hard',
    body: 'The camera is looking a little less often so the game keeps up. Moves still count.',
  },

  /** Grown-up-facing: GDS §36's fourth rung. */
  fallback: {
    // Not "For the grown-up": the pause overlay already opens with that phrase, and the copy
    // inventory guard caught the collision (two panels with one heading is a family reading the
    // same words twice and learning nothing from the second).
    title: 'Camera trouble',
    body: 'The camera is having trouble. You can carry on with the keyboard instead — the same mission, the same shards.',
    /** The election itself. */
    action: 'Carry on with the keyboard',
    /** Shown after the election, so nobody wonders where the camera went. */
    elected: 'The camera is off. The keys still play the whole mission.',
  },

  /** Two heroes, one camera (Slice E′, CAM-07). Child-facing, every word. */
  cooperative: {
    /**
     * The soft state: one hero's lane has gone quiet while the other keeps playing.
     *
     * It says what to do and nothing else. It does not say the mission is waiting, because it is
     * not, and it does not mention the other child at all.
     */
    laneComeBackBody: 'Step back in front of the camera whenever you are ready.',
    /** The same invitation for a hero who has not arrived yet — they have not been anywhere. */
    laneJoinBody: 'Stand in front of the camera whenever you are ready.',
    /** And for a hero the camera can see but cannot place: standing apart is what fixes it. */
    laneOwnSpotBody: 'Stand in your own spot so the suit can see you.',
    /** Why coming back is safe, said to the one child it is addressed to. */
    laneReassurance: 'Your number shards are safe, and your spot is waiting.',

    /**
     * The per-child answer, said out loud at last (Slice F′, retiring D-082(8)(a)).
     *
     * Slice E′ shipped this panel deliberately silent about the keyboard: the keys have been live
     * for both heroes the whole time (CAM-09), but saying so needs a *per-child* offer behind it,
     * and E′ had only the sitting-wide one. It has one now, so the true sentence can be said.
     *
     * Present tense and unconditional, because that is what is true: nothing has to be turned on,
     * nothing has to be waited for, and nothing is being taken away. It mentions no other child —
     * a hero reading their own panel learns about their own options and nothing about anybody
     * else's (non-competitive invariant; D-076(6) extends its spirit to input quality).
     */
    laneKeysAlways: 'Your keys work whenever you want them.',
    /**
     * Taking the keys for this hero and this hero only (D-076(5)'s per-child fallback).
     *
     * Worded as a choice a child is making for themselves, not as a repair and not as a loss. It
     * deliberately does not say "instead", which would frame the keys as the lesser thing.
     */
    laneKeysAction: 'Play with your keys',
    /**
     * What that hero reads afterwards.
     *
     * It promises exactly what is kept and no more: this sitting does not offer a way back to the
     * camera for one lane (a mid-sitting keys→camera upgrade is deliberately not built), so nothing
     * here hints that the camera is coming back for them.
     */
    laneKeysElected: 'Your keys are playing the whole mission.',

    /** The whole sitting is held: both heroes are out of the picture. */
    pauseBothTitle: 'Where did the heroes go?',
    pauseBothBody: 'Both heroes, step back in front of the camera so we can see you.',

    /**
     * The whole sitting is held because the camera cannot tell the two children apart — a crossing
     * that did not resolve, or two children on each other's sides. Standing apart in their own
     * halves is what fixes every one of those, so one invitation answers all of them.
     */
    pauseOwnSpotsTitle: 'Hop back to your own spots!',
    pauseOwnSpotsBody: 'Hero 1 on the left and Hero 2 on the right, so we can see you both.',
  },
} as const;

/** Every leaf string above, for the copy inventory guard. */
export const INVENTED_RECOVERY_STRINGS: readonly string[] = Object.values(
  INVENTED_RECOVERY_COPY,
).flatMap((group) => Object.values(group));

/** What the recovery surfaces are told about the situation they are describing. */
export type RecoveryInvitationReason =
  'ABSENT' | 'NEVER_ACQUIRED' | 'NO_FRAMES' | 'UNSURE_WHO' | null;

export interface RecoveryInvitation {
  readonly title: string;
  readonly body: string;
  readonly reassurance: string;
}

/**
 * Which invitation a held mission deserves (Slice G for one child, Slice E′ for two).
 *
 * Three of GDS §36's situations reach the same pause and only one of them is "you wandered off": a
 * child who never arrived has not gone anywhere, and a child whose camera stopped may be standing
 * exactly where they should be. Telling all three to step back in front of the camera would be the
 * game blaming a five-year-old for a cable.
 *
 * The cooperative branches change **only** the two situations where "you" would have to mean two
 * children. A camera that stopped, and a room nobody has walked into, already address a room rather
 * than a person, so both reuse the shipped words rather than inventing a second way to say them.
 */
export function recoveryInvitationFor(
  reason: RecoveryInvitationReason,
  options: { cooperative?: boolean } = {},
): RecoveryInvitation {
  const { overlay, cooperative } = INVENTED_RECOVERY_COPY;
  const together = options.cooperative === true;

  if (reason === 'NEVER_ACQUIRED') {
    return {
      title: overlay.inviteTitle,
      body: overlay.inviteBody,
      reassurance: overlay.inviteReassurance,
    };
  }
  if (reason === 'NO_FRAMES') {
    return {
      title: overlay.cameraStoppedTitle,
      body: overlay.cameraStoppedBody,
      reassurance: overlay.reassurance,
    };
  }
  if (reason === 'UNSURE_WHO') {
    return {
      title: cooperative.pauseOwnSpotsTitle,
      body: cooperative.pauseOwnSpotsBody,
      reassurance: overlay.reassurance,
    };
  }
  return together
    ? {
        title: cooperative.pauseBothTitle,
        body: cooperative.pauseBothBody,
        reassurance: overlay.reassurance,
      }
    : { title: overlay.title, body: overlay.body, reassurance: overlay.reassurance };
}

/**
 * What one hero reads when their own lane has gone quiet and the mission has **not** stopped.
 *
 * Deliberately without a title of its own: the heading is the child's own name, taken from the
 * session roster, so the panel is addressed to somebody rather than about them.
 */
export function laneInvitationFor(reason: RecoveryInvitationReason): {
  readonly body: string;
  readonly reassurance: string;
} {
  const { cooperative } = INVENTED_RECOVERY_COPY;
  const reassurance = cooperative.laneReassurance;

  if (reason === 'NEVER_ACQUIRED') return { body: cooperative.laneJoinBody, reassurance };
  if (reason === 'UNSURE_WHO') return { body: cooperative.laneOwnSpotBody, reassurance };
  if (reason === 'NO_FRAMES') {
    return { body: INVENTED_RECOVERY_COPY.overlay.cameraStoppedBody, reassurance };
  }
  return { body: cooperative.laneComeBackBody, reassurance };
}
